<template>
  <div class="form">
    <oeamtc-textarea
      id="details"
      placeholder="Hier können Sie zusätzliche Informationen zur Ursache angeben."
      v-model="form.FAULT_TEXT"
    >
      <template slot="label">Details:</template>
    </oeamtc-textarea>

    <div class="mapbox-wrapper">
      <MglMap
        :accessToken="accessToken"
        :mapStyle="mapStyle"
        :center="defaultCoordinates"
        :zoom="mapZoom"
        @load="onMapLoaded"
        @dragend="onMapMove"
        @zoomend="onMapMove"
      >
      </MglMap>
      <div class="mapbox-overlay"></div>
    </div>

    <oeamtc-input
      id="post_code"
      v-model="form.address.ZIPCODE"
      @blur="geocodeAddress"
      type="text"
    >
      <template slot="label">PLZ:</template>
    </oeamtc-input>
    <oeamtc-input
      id="city"
      v-model="form.address.CITY"
      @blur="geocodeAddress"
      type="text"
    >
      <template slot="label">Ort:</template>
    </oeamtc-input>
    <oeamtc-input
      id="address"
      type="text"
      v-model="form.address.STREET"
      @blur="geocodeAddress"
    >
      <template slot="label">Straße/Hausnummer:</template>
    </oeamtc-input>
    <p
      v-if="!gettingLocation"
      @click="locateMe"
      class="oeamtc-link oeamtc-field"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 16">
        <path
          d="M6 16a36.854 36.854 0 01-3-3.624C1.631 10.482 0 7.77 0 5.6A5.817 5.817 0 016 0a5.817 5.817 0 016 5.6c0 2.169-1.631 4.881-3 6.774A36.854 36.854 0 016 16zM6 3.6a2.24 2.24 0 00-1.516.585A1.924 1.924 0 003.856 5.6 2.077 2.077 0 006 7.6a2.211 2.211 0 001.515-.585A1.921 1.921 0 008.142 5.6 2.077 2.077 0 006 3.6z"
        />
      </svg>
      Aktuellen Standort bestimmen
    </p>
    <p v-else class="d-flex location-loading oeamtc-link oeamtc-field">
      <oeamtc-loader> Aktueller Standort wird ermittelt... </oeamtc-loader>
    </p>
    <h1 class="oeamtc-headline">Ihre Daten:</h1>
    <oeamtc-input
      id="clubcard"
      v-model="form.MEMBERNUMBER"
      :disabled="userLoaded"
      type="text"
    >
      <template slot="label">Clubkartenr:</template>
    </oeamtc-input>
    <oeamtc-input
      id="member_post_code"
      v-model="form.membernumber_zip"
      :disabled="userLoaded"
      type="text"
    >
      <template slot="label">PLZ (Wohnort):</template>
    </oeamtc-input>
    <oeamtc-input id="phone" v-model="form.CALLBACKNUMBER" type="text">
      <template slot="label">Telefonnummer:</template>
    </oeamtc-input>
    <div class="checkbox-wrapper">
      <oeamtc-checkbox id="reply-sms" v-model="sms" name="reply-sms">
        <template slot="label"
          >Ich bin gehörlos und möchte per Nachricht informiert werden</template
        >
      </oeamtc-checkbox>
    </div>
    <h1 class="oeamtc-headline">Betroffenes Fahrzeug:</h1>
    <oeamtc-dropdown
      id="selectedVehicle"
      v-model="selectedVehicle"
      :value="selectedVehicle"
      v-if="userVehicles.length > 0"
    >
      <template slot="label"> Wählen Sie das Fahrzeug aus: </template>
      <template slot="options">
        <option
          v-for="option in userVehicles"
          :key="option.id.toString()"
          :value="option.id.toString()"
        >
          {{ option.label }}
        </option>
      </template>
    </oeamtc-dropdown>
    <template v-if="selectedVehicle === '0'">
      <oeamtc-dropdown
        id="make"
        v-model="form.vehicle.BRAND"
        :value="form.vehicle.BRAND"
        @input="onMakesChange"
      >
        <template slot="label"> Marke: </template>
        <template slot="options">
          <option
            v-for="option in makes"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </template>
      </oeamtc-dropdown>
      <oeamtc-dropdown
        id="model"
        v-model="form.vehicle.TYPE"
        :value="form.vehicle.TYPE"
      >
        <template slot="label"> Modell: </template>
        <template slot="options">
          <option
            v-for="option in models"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </template>
      </oeamtc-dropdown>
      <oeamtc-input id="color" v-model="form.vehicle.COLOR" type="text">
        <template slot="label">Farbe: </template>
      </oeamtc-input>
      <oeamtc-input
        id="license_plate"
        v-model="form.vehicle.NUMBERPLATE"
        type="text"
      >
        <template slot="label">Kennzeichen: </template>
      </oeamtc-input>
    </template>
    <div v-if="error" class="error">
      {{ error }}
    </div>
    <div class="button-wrapper">
      <oeamtc-button tag="button" class="submit-button" @click="submitForm">
        <template v-if="submitDisabled">
          <oeamtc-loader></oeamtc-loader>
        </template>
        Nothilfe anfordern
      </oeamtc-button>
    </div>
  </div>
</template>

<script>
import {
  OeamtcTextarea,
  OeamtcInput,
  OeamtcDropdown,
  OeamtcButton,
  OeamtcLoader,
  OeamtcCheckbox,
} from '@oeamtc/styleguide-vuejs-lib-ui';
import Mapbox from 'mapbox-gl';
import { MglMap } from 'vue-mapbox';

import { mapMixin, mapboxMixin } from '@/mixins';
import axios from 'axios';

export default {
  name: 'RequestForm',
  mixins: [mapMixin, mapboxMixin],
  props: ['selectedCause', 'user', 'vehicles'],
  components: {
    OeamtcTextarea,
    OeamtcInput,
    OeamtcDropdown,
    OeamtcButton,
    OeamtcLoader,
    OeamtcCheckbox,
    MglMap,
  },
  data() {
    return {
      error: null,
      mapsApiKey: process.env.VUE_APP_GOOGLE_MAPS_API_KEY,
      nodeEnv: process.env.NODE_ENV,
      // defaultLocation: { lat: 47.635784, lng: 13.952637 },
      // mapOptions: { disableDefaultUI: true, zoomControl: true },
      mapZoom: 5,
      accessToken: process.env.VUE_APP_MAPBOX_ACCESS_TOKEN,
      mapStyle: process.env.VUE_APP_MAPBOX_STYLE,
      defaultCoordinates: [13.952637, 47.635784],

      makes: [],
      models: [],
      submitDisabled: false,
      userLoaded: false,
      userVehicles: [],
      gettingLocation: false,
      selectedVehicle: '0',
      sms: false,
      form: {
        MEMBERNUMBER: '',
        membernumber_zip: '',
        CALLBACKNUMBER: '',
        REPLY_MODE: 'PHONE',
        POS: {
          lat: '',
          lon: '',
        },
        address: {
          STREET: '',
          ZIPCODE: '',
          CITY: '',
        },
        vehicle: {
          BRAND: '',
          TYPE: '',
          NUMBERPLATE: '',
          COLOR: '',
        },
        FAULT: 0,
        FAULT_TEXT: '',
        PRIORITY: 0,
      },
    };
  },
  methods: {
    loadUserData() {
      if (this.user) {
        this.form.MEMBERNUMBER = this.user.info.member_info.cknr.toString();
        this.form.membernumber_zip = this.user.info.postal_address.plz;
        this.form.CALLBACKNUMBER = this.user.info.phone
          ? this.user.info.phone
          : '';
        this.userLoaded = true;
        if (
          this.user.vehicles_v2 &&
          Array.isArray(this.user.vehicles_v2) &&
          this.user.vehicles_v2.length > 0
        ) {
          this.userVehicles = this.user.vehicles_v2.map((vehicle) => {
            return {
              ...vehicle,
              label: `${vehicle.make.name} ${vehicle.model.name} ${vehicle.licensePlate.districtCode}-${vehicle.licensePlate.number}`,
            };
          });
          this.userVehicles.push({
            id: '0',
            label: 'Anderes Fahrzeug',
          });
          this.selectedVehicle = this.userVehicles[0].id.toString();
        }
      }
    },
    loadVehicles() {
      if (this.vehicles) {
        this.makes = this.vehicles.map((vehicleBrand, index) => {
          return { value: index.toString(), label: vehicleBrand.name };
        });
      }
    },
    onMakesChange() {
      this.models = [];
      this.models = [];
      this.form.vehicle.TYPE = '';
      const selectedBrandIndex = this.form.vehicle.BRAND;
      this.models = this.vehicles[selectedBrandIndex].models.map(
        (vehicleModel) => {
          return { value: vehicleModel.name, label: vehicleModel.name };
        }
      );
    },
    async getLocation() {
      return new Promise((resolve, reject) => {
        if (!('geolocation' in navigator)) {
          reject(new Error('Geolocation is not available.'));
        }

        navigator.geolocation.getCurrentPosition(
          (pos) => {
            resolve(pos);
          },
          (err) => {
            reject(err);
          }
        );
      });
    },
    async locateMe() {
      this.gettingLocation = true;
      try {
        const location = await this.getLocation();
        this.reverseGeocodeAddress(
          [location.coords.longitude, location.coords.latitude],
          true
        );
        this.gettingLocation = false;
      } catch (e) {
        this.gettingLocation = false;
        this.errorStr = e.message;
      }
    },
    geocodeAddress() {
      if (
        this.form.address.STREET &&
        this.form.address.ZIPCODE &&
        this.form.address.CITY
      ) {
        const address = `${this.form.address.STREET}, ${this.form.address.ZIPCODE} ${this.form.address.CITY}`;
        this.geocode(encodeURIComponent(address), (response) => {
          [this.form.POS.lon, this.form.POS.lat] = response.features[0].center;
          this.mapbox.flyTo({
            center: response.features[0].center,
            zoom: 16,
          });
        });
      }
    },
    reverseGeocodeAddress(coordinates, moveTo) {
      this.geocode(coordinates.toString(), (response) => {
        const address = this.getAddressComponentsMB(response.features[0]);
        this.form.address.STREET = address.street;
        this.form.address.ZIPCODE = address.postalCode;
        this.form.address.CITY = address.city;
        if (moveTo) {
          [this.form.POS.lon, this.form.POS.lat] = response.features[0].center;
          this.mapbox.flyTo({
            center: response.features[0].center,
            zoom: 16,
          });
        } else {
          this.form.POS.lon = coordinates[0];
          this.form.POS.lat = coordinates[1];
        }
      });
    },
    geocode(params, callback) {
      axios
        .get(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${params}.json?access_token=${this.accessToken}&language=de-DE`
        )
        .then(
          (response) => {
            callback(response.data);
          },
          (error) => {
            console.error(error);
          }
        );
    },
    onMapMove() {
      const center = this.mapbox.getCenter();
      this.reverseGeocodeAddress([center.lng, center.lat], false);
    },
    onMapLoaded(event) {
      // in component
      this.mapbox = event.map;
      this.locateMe();
    },
    submitForm() {
      this.submitDisabled = true;
      this.error = null;
      if (!this.form.MEMBERNUMBER) {
        alert(
          'Geben Sie bitte eine gültige Clubkartennummer an, um Nothilfe anzufordern.'
        );
        this.submitDisabled = false;
        return;
      }

      if (!this.form.membernumber_zip) {
        alert(
          'Geben Sie bitte die Postleitzahl Ihres Wohnortes an, um Nothilfe anzufordern.'
        );
        this.submitDisabled = false;
        return;
      }

      if (!this.form.CALLBACKNUMBER) {
        alert(
          'Geben Sie bitte eine Rückrufnummer an, um Nothilfe anzufordern.'
        );
        this.submitDisabled = false;
        return;
      }

      if (this.form.POS.lat === '' || this.form.POS.lon === '') {
        alert(
          'Geben Sie bitte die ungefähre Adresse Ihrer Panne an oder benutzen Sie die Karte, um Ihren Standort festzustellen.'
        );
        this.submitDisabled = false;
        return;
      }

      if (this.selectedVehicle === '0') {
        if (!this.form.vehicle.BRAND || !this.form.vehicle.TYPE) {
          alert('Geben Sie bitte ein Fahrzeug an, um Nothilfe anzufordern.');
          this.submitDisabled = false;
          return;
        }

        if (!this.form.vehicle.NUMBERPLATE) {
          alert(
            'Geben Sie bitte das Kennzeichen des betroffenen Fahrzeugs an, um Nothilfe anzufordern.'
          );
          this.submitDisabled = false;
          return;
        }
        if (!this.form.vehicle.COLOR) {
          alert(
            'Geben Sie bitte die Farbe des betroffenen Fahrzeugs an, um Nothilfe anzufordern.'
          );
          this.submitDisabled = false;
          return;
        }
      }

      this.form.FAULT = this.selectedCause.FAULT;

      const request = JSON.parse(JSON.stringify(this.form));
      if (this.sms) {
        request.REPLY_MODE = 'SMS';
      } else {
        request.REPLY_MODE = 'PHONE';
      }
      request.PRIORITY = 0;

      if (this.selectedVehicle !== '0') {
        const vehicle = this.userVehicles.find(
          (v) => v.id.toString() === this.selectedVehicle
        );
        request.vehicle = {
          BRAND: vehicle.make.name,
          TYPE: vehicle.model.name,
          NUMBERPLATE: `${vehicle.licensePlate.districtCode}${vehicle.licensePlate.number}`,
          COLOR: vehicle.farbe,
        };
      } else {
        const selectedBrandIndex = this.form.vehicle.BRAND;
        request.vehicle.BRAND = this.vehicles[selectedBrandIndex].name;
      }

      axios
        .post(`${process.env.VUE_APP_API_URL}/msg/assistance/incident`, {
          AssistanceRequest: request,
        })
        .then(
          (response) => {
            this.$emit('submitted', response.data);
            this.submitDisabled = false;
          },
          (error) => {
            console.error(error);
            const errorResponse =
              error && error.response && error.response.data
                ? error.response.data
                : null;
            if (
              errorResponse._status &&
              errorResponse._status['code_message']
            ) {
              this.error = errorResponse._status['code_message'];
            } else {
              this.error = `Ein allgemeiner Fehler ist aufgetreten.`;
            }
            this.submitDisabled = false;
          }
        );
    },
  },
  beforeMount() {
    this.loadVehicles();
    this.loadUserData();
  },
  created() {
    this.mapbox = Mapbox;
  },
};
</script>
<style lang="scss" scoped>
.map-wrapper {
  height: 300px;
  position: relative;
}
#form-map {
  height: 100%;
  border-radius: $default-radius;
  overflow: hidden;
}
.priority-wrapper {
  margin-top: 20px;
}
.checkbox-wrapper {
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  :deep(.oeamtc-choice + label) {
    text-decoration: none;
    cursor: pointer;
  }
}
.oeamtc-link {
  svg {
    width: 10px;
    fill: #0071bb;
  }
}
.location-loading {
  :deep(.oeamtc-defaultloader__text) {
    font-size: 1rem;
  }
  :deep(.oeamtc-defaultloader__icon) svg {
    width: 1rem;
    height: 1rem;
  }
}
.oeamtc-headline {
  margin-top: 0.5rem;
}

.mapbox-wrapper {
  height: 300px;
  position: relative;
  .mapbox-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 78px;
    height: 78px;
    background: url('~@/assets/icons/marker.png');
    background-size: 78px 78px;
    background-repeat: no-repeat;
    background-position: center;
    transform: translate(-50%, -50%);
    pointer-events: none;
  }
}

.position-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 78px;
  height: 78px;
  background: url('~@/assets/icons/marker.png');
  background-size: 78px 78px;
  background-repeat: no-repeat;
  background-position: center;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.submit-button {
  margin: 20px auto;
  display: flex;
}

.button-wrapper {
  display: flex;
  justify-content: center;
}

.error {
  text-align: center;
  color: red;
}
</style>
